import { ReactNode } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useColorModeValue,
  Container,
  Avatar,
  Text,
  useToast,
  Heading,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';
import { ColorModeToggle as ColorModeSwitcher } from './ColorModeToggle';
import { useUserData } from '../hooks/useUserData';
import { DiscordLogo } from '@phosphor-icons/react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useSocial } from '../contexts/SocialContext';

interface Props {
  children: ReactNode;
}

const Layout = ({ children }: Props) => {
  useAnalytics(); // Track all page views automatically
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentUser, logout } = useAuth();
  const { userData } = useUserData();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const { pendingFriendRequestCount, unreadMessageCount } = useSocial();
  const bgColor = useColorModeValue('bg.emphasized', 'bg.emphasized');
  const borderColor = useColorModeValue('border.default', 'border.default');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast({
        title: t('auth.logoutSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t('auth.logoutError'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const NavLink = ({ to, children, onClick }: { to: string; children: ReactNode; onClick?: () => void }) => (
    <RouterLink to={to} onClick={onClick}>
      <Text
        px={2}
        py={1}
        rounded={'md'}
        _hover={{
          textDecoration: 'none',
          bg: useColorModeValue('gray.200', 'gray.700'),
        }}
      >
        {children}
      </Text>
    </RouterLink>
  );

  return (
    <Box minH="100vh" position="relative" pb="60px">
      <Box
        bg={bgColor}
        px={4}
        position="fixed"
        w="100%"
        zIndex={100}
        borderBottom="1px"
        borderStyle={'solid'}
        borderColor={borderColor}
      >
        <Container maxW="container.xl">
          <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
            <IconButton
              size={'md'}
              icon={<HamburgerIcon />}
              aria-label={'Open Menu'}
              display={{ md: 'none' }}
              onClick={onOpen}
              variant="ghost"
              color="text.muted"
            />
            <HStack spacing={8} alignItems={'center'}>
              <Heading as={RouterLink} to="/" size="md" cursor="pointer" color="text.default">
                CubersDen
              </Heading>
              <HStack as={'nav'} spacing={4} display={{ base: 'none', md: 'flex' }}>
                <NavLink to="/timer">{t('nav.timer')}</NavLink>
                <Menu closeOnBlur={true} closeOnSelect={true}>
                  {({ onClose }) => (
                    <>
                      <MenuButton
                        as={Button}
                        variant="ghost"
                        rightIcon={<ChevronDownIcon />}
                      >
                        {t('nav.learn')}
                      </MenuButton>
                      <MenuList>
                        <MenuItem as={RouterLink} to="/beginners-method" onClick={onClose}>
                          {t('nav.beginnersMethod')}
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/cfop" onClick={onClose}>
                          {t('nav.cfopMethod')}
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem as={RouterLink} to="/oll" onClick={onClose}>
                          {t('nav.ollMethod')}
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/pll" onClick={onClose}>
                          {t('nav.pllMethod')}
                        </MenuItem>
                        <MenuDivider />
                        <MenuItem as={RouterLink} to="/moves-notation" onClick={onClose}>
                          {t('nav.movesNotation')}
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/history" onClick={onClose}>
                          {t('nav.history')}
                        </MenuItem>
                      </MenuList>
                    </>
                  )}
                </Menu>
                <Menu closeOnBlur={true} closeOnSelect={true}>
                  {({ onClose }) => (
                    <>
                      <MenuButton
                        as={Button}
                        variant="ghost"
                        rightIcon={<ChevronDownIcon />}
                      >
                        {t('nav.tools')}
                      </MenuButton>
                      <MenuList>
                        <MenuItem as={RouterLink} to="/solve" onClick={onClose}>
                          {t('nav.cubeSolver')}
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/training" onClick={onClose}>
                          {t('nav.algorithmTraining')}
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/timer/zen" onClick={onClose}>
                          {t('nav.zenMode')}
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/shared" onClick={onClose}>
                          {t('nav.shared')}
                        </MenuItem>
                      </MenuList>
                    </>
                  )}
                </Menu>
                {currentUser && (
                  <Menu closeOnBlur={true} closeOnSelect={true}>
                    {({ onClose }) => (
                      <>
                        <MenuButton
                          as={Button}
                          variant="ghost"
                          rightIcon={<ChevronDownIcon />}
                        >
                          <HStack spacing={1}>
                            <Text>{t('nav.social')}</Text>
                            {(pendingFriendRequestCount + unreadMessageCount) > 0 && (
                              <Box
                                bg="red.500"
                                color="white"
                                borderRadius="full"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                px={1}
                              >
                                {pendingFriendRequestCount + unreadMessageCount}
                              </Box>
                            )}
                          </HStack>
                        </MenuButton>
                        <MenuList>
                          <MenuItem as={RouterLink} to="/feed" onClick={onClose}>
                            {t('nav.feed')}
                            {pendingFriendRequestCount > 0 && (
                              <Box
                                as="span"
                                bg="red.500"
                                color="white"
                                borderRadius="full"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                ml={2}
                                px={1}
                              >
                                {pendingFriendRequestCount}
                              </Box>
                            )}
                          </MenuItem>
                          <MenuItem as={RouterLink} to="/battles" onClick={onClose}>
                            {t('nav.battles')}
                          </MenuItem>
                          <MenuItem as={RouterLink} to="/leaderboard" onClick={onClose}>
                            {t('nav.leaderboard')}
                          </MenuItem>
                          <MenuItem as={RouterLink} to="/messages" onClick={onClose}>
                            {t('nav.messages')}
                            {unreadMessageCount > 0 && (
                              <Box
                                as="span"
                                bg="red.500"
                                color="white"
                                borderRadius="full"
                                fontSize="xs"
                                minW="18px"
                                h="18px"
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                ml={2}
                                px={1}
                              >
                                {unreadMessageCount}
                              </Box>
                            )}
                          </MenuItem>
                        </MenuList>
                      </>
                    )}
                  </Menu>
                )}
                <NavLink to="/cubes">{t('nav.cubes')}</NavLink>
              </HStack>
            </HStack>

            <Flex alignItems={'center'}>
              <HStack spacing={3}>
                <Box display={{ base: 'none', md: 'block' }}>
                  <LanguageSwitcher />
                </Box>
                <ColorModeSwitcher />
                {currentUser ? (
                  <Menu closeOnBlur={true}>
                    <MenuButton
                      as={Button}
                      rounded={'full'}
                      variant={'link'}
                      cursor={'pointer'}
                      minW={0}
                    >
                      <Avatar
                        size={'sm'}
                        name={currentUser.displayName || currentUser.email || ''}
                        src={currentUser.photoURL || undefined}
                      />
                    </MenuButton>
                    <MenuList>
                      <MenuItem as={RouterLink} to="/profile">
                        {t('nav.profile')}
                      </MenuItem>
                      <MenuItem as={RouterLink} to="/statistics">
                        {t('nav.statistics')}
                      </MenuItem>
                      <MenuItem as={RouterLink} to="/tournament">
                        {t('nav.tournament')}
                      </MenuItem>
                      {userData?.isAdmin && (
                        <>
                          <MenuDivider />
                          <MenuItem as={RouterLink} to="/admin">
                            {t('nav.adminDashboard')}
                          </MenuItem>
                          <MenuItem as={RouterLink} to="/api-docs">
                            {t('nav.apiDocs')}
                          </MenuItem>
                          <MenuItem as={RouterLink} to="/hardware-test">
                            {t('nav.hardwareTest')}
                          </MenuItem>
                        </>
                      )}
                      <MenuDivider />
                      <MenuItem onClick={() => {
                        handleLogout();
                      }}>{t('nav.logout')}</MenuItem>
                    </MenuList>
                  </Menu>
                ) : (
                  <Button
                    as={RouterLink}
                    to="/login"
                    variant={'solid'}
                    colorScheme={'brand'}
                    size={'sm'}
                    display={{ base: 'none', md: 'flex' }}
                  >
                    {t('nav.login')}
                  </Button>
                )}
              </HStack>
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Heading size="md">CubersDen</Heading>
          </DrawerHeader>

          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <NavLink to="/timer" onClick={onClose}>{t('nav.timer')}</NavLink>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color="text.muted" mb={2}>
                  {t('nav.learn')}
                </Text>
                <VStack align="stretch" pl={2} spacing={2}>
                  <NavLink to="/beginners-method" onClick={onClose}>{t('nav.beginnersMethod')}</NavLink>
                  <NavLink to="/cfop" onClick={onClose}>{t('nav.cfopMethod')}</NavLink>
                  <NavLink to="/oll" onClick={onClose}>{t('nav.ollMethod')}</NavLink>
                  <NavLink to="/pll" onClick={onClose}>{t('nav.pllMethod')}</NavLink>
                  <NavLink to="/moves-notation" onClick={onClose}>{t('nav.movesNotation')}</NavLink>
                  <NavLink to="/history" onClick={onClose}>{t('nav.history')}</NavLink>
                </VStack>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color="text.muted" mb={2}>
                  {t('nav.tools')}
                </Text>
                <VStack align="stretch" pl={2} spacing={2}>
                  <NavLink to="/solve" onClick={onClose}>{t('nav.cubeSolver')}</NavLink>
                  <NavLink to="/training" onClick={onClose}>{t('nav.algorithmTraining')}</NavLink>
                  <NavLink to="/timer/zen" onClick={onClose}>{t('nav.zenMode')}</NavLink>
                  <NavLink to="/shared" onClick={onClose}>{t('nav.shared')}</NavLink>
                </VStack>
              </Box>

              <Box>
                <NavLink to="/cubes" onClick={onClose}>{t('nav.cubes')}</NavLink>
              </Box>

              {currentUser && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="text.muted" mb={2}>
                    {t('nav.social')}
                  </Text>
                  <VStack align="stretch" pl={2} spacing={2}>
                    <NavLink to="/feed" onClick={onClose}>
                      {t('nav.feed')}
                    </NavLink>
                    <NavLink to="/battles" onClick={onClose}>
                      {t('nav.battles')}
                    </NavLink>
                    <NavLink to="/leaderboard" onClick={onClose}>
                      {t('nav.leaderboard')}
                    </NavLink>
                    <NavLink to="/messages" onClick={onClose}>
                      {t('nav.messages')}
                    </NavLink>
                  </VStack>
                </Box>
              )}

              <Box>
                <Text fontSize="sm" fontWeight="medium" color="text.muted" mb={2}>
                  {t('profile.settings')}
                </Text>
                <VStack align="stretch" pl={2} spacing={2}>
                  <Box py={2}>
                    <LanguageSwitcher />
                  </Box>
                </VStack>
              </Box>

              {currentUser && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="text.muted" mb={2}>
                    {t('nav.account')}
                  </Text>
                  <VStack align="stretch" pl={2} spacing={2}>
                    <NavLink to="/profile" onClick={onClose}>{t('nav.profile')}</NavLink>
                    <NavLink to="/statistics" onClick={onClose}>{t('nav.statistics')}</NavLink>
                    <NavLink to="/tournament" onClick={onClose}>{t('nav.tournament')}</NavLink>
                    {userData?.isAdmin && (
                      <>
                        <NavLink to="/admin" onClick={onClose}>{t('nav.adminDashboard')}</NavLink>
                        <NavLink to="/api-docs" onClick={onClose}>{t('nav.apiDocs')}</NavLink>
                        <NavLink to="/hardware-test" onClick={onClose}>{t('nav.hardwareTest')}</NavLink>
                      </>
                    )}
                    <Box
                      px={2}
                      py={1}
                      rounded={'md'}
                      _hover={{
                        bg: useColorModeValue('gray.200', 'gray.700'),
                      }}
                      cursor="pointer"
                      onClick={() => {
                        handleLogout();
                        onClose();
                      }}
                    >
                      {t('nav.logout')}
                    </Box>
                  </VStack>
                </Box>
              )}

              {!currentUser && (
                <Box>
                  <Button
                    as={RouterLink}
                    to="/login"
                    variant={'solid'}
                    colorScheme={'brand'}
                    size={'sm'}
                    width="full"
                    onClick={onClose}
                  >
                    {t('nav.login')}
                  </Button>
                </Box>
              )}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Main content */}
      <Box pt="80px">
        <Container maxW="container.xl" pb={8}>
          {children}
        </Container>
      </Box>

      {/* Footer */}
      <Box
        as="footer"
        position="absolute"
        bottom="0"
        width="100%"
        py={4}
        bg={bgColor}
        borderTop="1px"
        borderStyle="solid"
        borderColor={borderColor}
      >
        <Container maxW="container.xl">
          <Flex 
            direction={{ base: 'column', md: 'row' }} 
            justify="space-between" 
            align={{ base: 'center', md: 'center' }}
            gap={4}
          >
            <Text 
              fontSize="sm" 
              color="text.muted"
              textAlign={{ base: 'center', md: 'left' }}
            >
              CubersDen.com | Copyright © {new Date().getFullYear()} Arvind Juneja
            </Text>
            <Button
              as="a"
              href="https://discord.gg/ER69A6UN"
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
              colorScheme="brand"
              leftIcon={<DiscordLogo size={20} weight="bold" />}
              size={{ base: 'sm', md: 'md' }}
              width={{ base: 'full', md: 'auto' }}
            >
              {t('footer.joinCommunity')}
            </Button>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout; 